// src/components/Navbar.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiMenu,
  FiX,
  FiPlusCircle,
  FiPackage,
  FiMail,
  FiShield,
  FiUserCheck,
  FiChevronDown,
  FiPhoneCall,
  FiInfo,
  FiXCircle,
  FiLogIn,
  FiEdit3,
} from "react-icons/fi";

// Firestore (for announcement bar)
import app from "../firebase";
import { getFirestore, doc, onSnapshot, Timestamp } from "firebase/firestore";

const db = getFirestore(app);

const Navbar = () => {
  const { pathname } = useLocation();

  // --- existing nav state ---
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [mobileDropdowns, setMobileDropdowns] = useState({
    medicare: false,
    life: false,
    brokers: false,
  });

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const toggleMobileDropdown = (section) =>
    setMobileDropdowns((prev) => ({ ...prev, [section]: !prev[section] }));

  const handleMouseEnter = (menu) => {
    clearTimeout(hoverTimeout);
    setHoveredDropdown(menu);
  };
  const handleMouseLeave = () => {
    const timeout = setTimeout(() => setHoveredDropdown(null), 150);
    setHoverTimeout(timeout);
  };

  // --- helpers ---
  const navItem = (to, label) => {
    const isActive = pathname === to;
    return (
      <Link
        to={to}
        className={`px-3 py-2 rounded-md transition font-semibold text-sm ${
          isActive ? "bg-[#e31c3d] text-white" : "text-white hover:bg-[#e31c3d]/90"
        }`}
        onClick={() => setMenuOpen(false)}
      >
        {label}
      </Link>
    );
  };

  const dropdownLink = (to, label, Icon) => {
    const isActive = pathname === to;
    return (
      <Link
        to={to}
        onClick={() => setHoveredDropdown(null)}
        className={`flex items-center gap-3 px-5 py-3 rounded-md transition text-sm font-medium ${
          isActive ? "bg-[#e31c3d] text-white" : "text-[#002b5c] hover:bg-blue-100"
        }`}
      >
        <Icon className="text-lg" /> {label}
      </Link>
    );
  };

  // -------------------------------
  // Announcement Bar (Firestore)
  // -------------------------------
  const [announcement, setAnnouncement] = useState(null);
  const [dismissedKey, setDismissedKey] = useState("");

  useEffect(() => {
    const ref = doc(db, "site_settings", "announcementBar");
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setAnnouncement(null);
        setDismissedKey("");
        return;
      }
      const data = snap.data() || {};
      const updatedAt =
        data.updatedAt instanceof Timestamp ? data.updatedAt.toMillis() : Date.now();
      const key = `announcement_dismissed_v${updatedAt}`;

      setAnnouncement({
        enabled: Boolean(data.enabled),
        message: data.message || "",
        linkUrl: data.linkUrl || "",
        linkLabel: data.linkLabel || "",
        dismissible: Boolean(data.dismissible),
        startAt: data.startAt instanceof Timestamp ? data.startAt.toMillis() : null,
        endAt: data.endAt instanceof Timestamp ? data.endAt.toMillis() : null,
        updatedAt,
      });
      setDismissedKey(key);
    });

    return () => unsub();
  }, []);

  const isActiveNow = useMemo(() => {
    if (!announcement) return false;
    if (!announcement.enabled) return false;
    const now = Date.now();
    if (announcement.startAt && now < announcement.startAt) return false;
    if (announcement.endAt && now > announcement.endAt) return false;
    return true;
  }, [announcement]);

  const isDismissed = useMemo(() => {
    if (!announcement || !dismissedKey) return false;
    return localStorage.getItem(dismissedKey) === "1";
  }, [announcement, dismissedKey]);

  const dismissAnnouncement = () => {
    if (!dismissedKey) return;
    localStorage.setItem(dismissedKey, "1");
    setAnnouncement((a) => (a ? { ...a } : a));
  };

  // shadow on scroll
  useEffect(() => {
    const handleScroll = () => {
      const nav = document.getElementById("navbar");
      if (window.scrollY > 10) nav?.classList.add("shadow-lg");
      else nav?.classList.remove("shadow-lg");
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="relative z-50">
      <nav id="navbar" className="sticky top-0 bg-[#002b5c] text-white transition-shadow duration-300 z-50">
        {/* --- MERGED TOP STRIP (Phone CTA + Announcement) --- */}
        <div className="bg-[#e31c3d] text-white border-b border-red-800">
          <div className="max-w-7xl mx-auto px-6 py-2 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4">
            {/* Phone CTA */}
            <div className="text-sm md:text-base font-medium tracking-wide">
              <FiPhoneCall className="inline-block mr-2" />
              <span className="opacity-95">Questions? Call us now </span>
              <a href="tel:+19143584068" className="font-bold underline hover:text-yellow-200 ml-1">
                +1 (914) 358-4068
              </a>
            </div>

            {/* Announcement (conditionally rendered) */}
            {isActiveNow && !isDismissed && (
              <div className="w-full md:w-auto">
                <div className="relative flex items-center gap-2 md:gap-3 rounded-md bg-white/10 px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm">
                  <span className="inline-flex items-center gap-2 font-semibold tracking-wide">
                    <FiInfo className="text-white/90" />
                    <span className="uppercase opacity-90">Announcement</span>
                  </span>

                  <span className="h-3 w-[1px] bg-white/30 hidden md:inline-block" />

                  <p className="flex-1 leading-snug">
                    {announcement.message}
                    {announcement.linkUrl ? (
                      <>
                        {" "}
                        {announcement.linkUrl.startsWith("http") ? (
                          <a
                            href={announcement.linkUrl}
                            className="underline font-semibold hover:text-yellow-200"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {announcement.linkLabel || "Learn more"}
                          </a>
                        ) : (
                          <Link to={announcement.linkUrl} className="underline font-semibold hover:text-yellow-200">
                            {announcement.linkLabel || "Learn more"}
                          </Link>
                        )}
                      </>
                    ) : null}
                  </p>

                  {announcement.dismissible && (
                    <button
                      onClick={dismissAnnouncement}
                      className="shrink-0 rounded-full p-1 hover:bg-white/20 transition"
                      aria-label="Dismiss announcement"
                      title="Dismiss"
                    >
                      <FiXCircle className="text-base md:text-lg" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* --- Main bar --- */}
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center relative">
          <Link to="/" className="flex items-center space-x-4 px-2">
            <img src="/images/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex space-x-3 items-center">
            {navItem("/", "Home")}
            {navItem("/about", "About")}

            {/* Medicare dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter("medicare")}
              onMouseLeave={handleMouseLeave}
            >
              <button className="px-3 py-2 rounded-md text-sm font-semibold hover:bg-[#e31c3d]/90 transition">
                Medicare Options
              </button>
              {hoveredDropdown === "medicare" && (
                <div className="absolute bg-white text-[#002b5c] shadow-md mt-2 rounded-md w-72 z-50">
                  {dropdownLink("/medicare-a", "Medicare Advantage Plans", FiPlusCircle)}
                  {dropdownLink("/medicare-d", "Part D Drug Plans", FiPackage)}
                  {dropdownLink("/medicare-supplements", "Medicare Supplements", FiShield)}
                  {/* Plan help → /start */}
                  {dropdownLink("/start", "Start", FiUserCheck)}
                </div>
              )}
            </div>

            {/* New top-level pages */}
            {navItem("/health-insurance", "Health Insurance")}

            {/* Life dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter("life")}
              onMouseLeave={handleMouseLeave}
            >
              <button className="px-3 py-2 rounded-md text-sm font-semibold hover:bg-[#e31c3d]/90 transition">
                Life Insurance Options
              </button>
              {hoveredDropdown === "life" && (
                <div className="absolute bg-white text-[#002b5c] shadow-md mt-2 rounded-md w-72 z-50">
                  {dropdownLink("/term-life", "Term Life Insurance", FiShield)}
                  {dropdownLink("/permanent-life", "Permanent Life Insurance", FiUserCheck)}
                  {/* Plan help → /start */}
                  {dropdownLink("/start", "Start", FiMail)}
                </div>
              )}
            </div>

            {navItem("/help", "Help")}
            {navItem("/how-to-become-partner", "How To Become A Partner")}
            {navItem("/contact", "Contact")}
            {navItem("/blog", "Blog")}

            {/* Brokers dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter("brokers")}
              onMouseLeave={handleMouseLeave}
            >
              <button className="px-3 py-2 rounded-md text-sm font-semibold hover:bg-[#e31c3d]/90 transition">
                Brokers
              </button>
              {hoveredDropdown === "brokers" && (
                <div className="absolute bg-white text-[#002b5c] shadow-md mt-2 rounded-md w-56 z-50">
                  {dropdownLink("/broker-login", "Broker Login", FiLogIn)}
                  {dropdownLink("/how-to-become-partner", "Become a Broker", FiEdit3)}
                </div>
              )}
            </div>

            {/* Start CTA */}
            <Link
              to="/start"
              className={`ml-2 inline-flex items-center px-4 py-2 rounded-xl font-semibold text-sm ${
                pathname === "/start"
                  ? "bg-[#ef3b51] text-white"
                  : "bg-[#ef3b51] text-white hover:bg-[#d72f44]"
              }`}
            >
              Start
            </Link>
          </div>

          {/* Mobile trigger */}
          <div className="md:hidden z-50">
            <button onClick={toggleMenu} className="p-2 rounded-full hover:bg-white/10 transition">
              {menuOpen ? <FiX className="text-2xl text-white" /> : <FiMenu className="text-2xl text-white" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`absolute left-0 w-full bg-[#002b5c] text-white transition-transform duration-300 ease-in-out origin-top transform ${
            menuOpen ? "scale-y-100" : "scale-y-0"
          }`}
          style={{ transformOrigin: "top" }}
        >
          <div className="max-h-[80vh] overflow-y-auto px-4 pt-6 pb-8 space-y-4 text-sm scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {/* Quick links */}
            <div className="flex flex-wrap gap-2">
              {navItem("/", "Home")}
              {navItem("/about", "About")}
              {navItem("/health-insurance", "Health Insurance")}
              {navItem("/help", "Help")}
            </div>

            {/* Medicare mobile dropdown */}
            <div className="bg-white text-[#002b5c] rounded-xl shadow">
              <button
                onClick={() => toggleMobileDropdown("medicare")}
                className="w-full flex justify-between items-center px-4 py-3 font-bold text-sm"
              >
                Medicare Options
                <FiChevronDown
                  className={`text-xl transition-transform duration-300 ${
                    mobileDropdowns.medicare ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  mobileDropdowns.medicare
                    ? "max-h-96 opacity-100 scale-y-100 py-2 px-4"
                    : "max-h-0 opacity-0 scale-y-95"
                } border-t border-gray-200 space-y-3`}
              >
                {dropdownLink("/medicare-a", "Medicare Advantage Plans", FiPlusCircle)}
                {dropdownLink("/medicare-d", "Part D Drug Plans", FiPackage)}
                {dropdownLink("/medicare-supplements", "Medicare Supplements", FiShield)}
                {/* Plan help → /start */}
                {dropdownLink("/start", "Start", FiUserCheck)}
              </div>
            </div>

            {/* Life mobile dropdown */}
            <div className="bg-white text-[#002b5c] rounded-xl shadow">
              <button
                onClick={() => toggleMobileDropdown("life")}
                className="w-full flex justify-between items-center px-4 py-3 font-bold text-sm"
              >
                Life Insurance Options
                <FiChevronDown
                  className={`text-xl transition-transform duration-300 ${
                    mobileDropdowns.life ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  mobileDropdowns.life
                    ? "max-h-96 opacity-100 scale-y-100 py-2 px-4"
                    : "max-h-0 opacity-0 scale-y-95"
                } border-t border-gray-200 space-y-3`}
              >
                {dropdownLink("/term-life", "Term Life Insurance", FiShield)}
                {dropdownLink("/permanent-life", "Permanent Life Insurance", FiUserCheck)}
                {/* Plan help → /start */}
                {dropdownLink("/start", "Start", FiMail)}
              </div>
            </div>

            {/* Brokers mobile dropdown */}
            <div className="bg-white text-[#002b5c] rounded-xl shadow">
              <button
                onClick={() => toggleMobileDropdown("brokers")}
                className="w-full flex justify-between items-center px-4 py-3 font-bold text-sm"
              >
                Brokers
                <FiChevronDown
                  className={`text-xl transition-transform duration-300 ${
                    mobileDropdowns.brokers ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  mobileDropdowns.brokers
                    ? "max-h-96 opacity-100 scale-y-100 py-2 px-4"
                    : "max-h-0 opacity-0 scale-y-95"
                } border-t border-gray-200 space-y-3`}
              >
                {dropdownLink("/broker-login", "Broker Login", FiLogIn)}
                {dropdownLink("/broker-signup", "Become a Broker", FiEdit3)}
              </div>
            </div>

            {/* Other links */}
            <div className="flex flex-col space-y-2">
              {navItem("/how-to-become-partner", "How To Become A Partner")}
              {navItem("/contact", "Contact")}
              {navItem("/blog", "Blog")}
            </div>

            {/* Big Start button on mobile */}
            <Link
              to="/start"
              onClick={() => setMenuOpen(false)}
              className="block w-full text-center mt-2 px-4 py-3 rounded-xl bg-[#ef3b51] font-semibold hover:bg-[#d72f44]"
            >
              Start
            </Link>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
